import {Routes} from "@angular/router"
import {PriceMappingComponent} from "@pricing/components/price-mapping/price-mapping.component"
import {TemplateViewerDialogComponent} from "@common/components/dialogs/template-viewer-dialog/template-viewer-dialog.component"
import {
    AuthGuard,
    OnboardingGuard,
    OrganizationAdminGuard,
    PicturesAuthGuard,
    RedirectIfAuthenticatedGuard,
    StaffGuard,
    SubscriptionGuard,
} from "@common/services/guards/guards.service"
import {JobFarmInstancesPageComponent} from "@pages/job-farm-instances/job-farm-instances-page.component"
import {AssetDetailsComponent} from "@platform/components/assets/asset-details/asset-details.component"
import {FileDetailsComponent} from "@platform/components/files/file-details/file-details.component"
import {HdriDetailsComponent} from "@platform/components/hdris/hdri-details/hdri-details.component"
import {JobDetailsComponent} from "@platform/components/jobs/job-details/job-details.component"
import {JobInstanceDetailsComponent} from "@platform/components/jobs/job-instance-details/job-instance-details.component"
import {JobTaskDetailsComponent} from "@platform/components/jobs/job-task-details/job-task-details.component"
import {ModelDetailsComponent} from "@platform/components/models/model-details/model-details.component"
import {OrganizationDetailsComponent} from "@platform/components/organizations/organization-details/organization-details.component"
import {PictureDetailsComponent} from "@platform/components/pictures/picture-details/picture-details.component"
import {PictureViewerDialogComponent} from "@platform/components/pictures/picture-viewer-dialog/picture-viewer-dialog.component"
import {PicturesPageComponent} from "@pages/pictures/pictures-page.component"
import {ScanningLogsComponent} from "@platform/components/scanning/scanning-logs/scanning-logs.component"
import {ScanningComponent} from "@platform/components/scanning/scanning/scanning.component"
import {TagDetailsComponent} from "@platform/components/tags/tag-details/tag-details.component"
import {TaskTypesPageComponent} from "@pages/task-types/task-types-page.component"
import {TaskDetailsComponent} from "@platform/components/tasks/task-details/task-details.component"
import {UserDetailsComponent} from "@platform/components/users/user-details/user-details.component"
import {AssetsPageComponent} from "@pages/assets/assets-page.component"
import {FilesPageComponent} from "@pages/files/files-page.component"
import {HdrisPageComponent} from "@pages/hdris/hdris-page.component"
import {JobFarmGroupsPageComponent} from "@pages/job-farm-groups/job-farm-groups-page.component"
import {JobsPageComponent} from "@pages/jobs/jobs-page.component"
import {LoginPageComponent} from "@pages/login/login-page.component"
import {TenantLoginPageComponent} from "@pages/login/tenant-login-page.component"
import {SignUpPageComponent} from "@pages/sign-up/sign-up-page.component"
import {VerifyEmailPageComponent} from "@pages/verify-email/verify-email-page.component"
import {OnboardingPageComponent} from "@pages/onboarding/onboarding-page.component"
import {ModelsPageComponent} from "@pages/models/models-page.component"
import {OrganizationGroupsPageComponent} from "@pages/organization-groups/organization-groups-page.component"
import {OrganizationsPageComponent} from "@pages/organizations/organizations-page.component"
import {PageNotFoundComponent} from "@pages/page-not-found/page-not-found.component"
import {StatisticsPageComponent} from "@pages/statistics/statistics-page.component"
import {TagsPageComponent} from "@pages/tags/tags-page.component"
import {UsersPageComponent} from "@pages/users/users-page.component"
import {LegacyPictureResolve} from "@platform/services/resolve/resolve.service"
import {TemplateEditorComponent} from "@template-editor/components/template-editor/template-editor.component"
import {UnauthorizedComponent} from "@pages/unauthorized/unauthorized.component"
import {BackendArGenerationComponent} from "@pages/ar-generation/backend-ar-generation.component"
import {TestBaseComponent} from "@app/test/manual-testing/test-base/test-base.component"
import {ConfiguratorTestComponentsComponent} from "@app/test/manual-testing/configurator-test-components/configurator-test-components.component"
import {HandleSsoCallbackComponent} from "@common/components/handle-sso-callback/handle-sso-callback.component"
import {HandleSsoSignupCallbackComponent} from "@common/components/handle-sso-signup-callback/handle-sso-signup-callback.component"
import {OrganizationSettingsComponent} from "@pages/settings/organization/organization-settings-page.component"
import {InactiveSubscriptionPageComponent} from "@pages/inactive-subscription/inactive-subscription-page.component"
import {materialsRouteBase, templateRouteBase} from "@app/platform/config/route-helpers"

export const routes: Routes = [
    {path: "", redirectTo: "/pictures", pathMatch: "full"},

    {
        path: "assets",
        title: "Assets - colormass",
        component: AssetsPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            {
                path: ":itemId",
                component: AssetDetailsComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [
                    {
                        path: "tasks/:taskId",
                        component: TaskDetailsComponent,
                        canActivate: [AuthGuard, SubscriptionGuard],
                        data: {closeNavigationPath: "../../"},
                    },
                    {
                        path: "template/:templateId",
                        component: TemplateViewerDialogComponent,
                        data: {closeNavigationPath: "../../"},
                        canActivate: [AuthGuard, SubscriptionGuard],
                    },
                ],
            },
        ],
    },

    {
        path: "backend-ar-generation",
        component: BackendArGenerationComponent,
        canActivate: [AuthGuard],
    },

    {
        path: "configurator-components-testing",
        component: ConfiguratorTestComponentsComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
    },

    {
        path: "files",
        title: "Files - colormass",
        component: FilesPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            {
                path: ":itemId",
                component: FileDetailsComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
            },
        ],
    },

    {
        path: "hdris",
        title: "HDRIs - colormass",
        component: HdrisPageComponent,
        canActivate: [StaffGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [StaffGuard],
                children: [],
            },
            {
                path: ":itemId",
                component: HdriDetailsComponent,
                canActivate: [StaffGuard],
            },
        ],
    },

    {
        path: "inactive-subscription",
        title: "Subscription inactive - colormass",
        component: InactiveSubscriptionPageComponent,
        canActivate: [AuthGuard],
    },

    {
        path: "jobs",
        title: "Jobs - colormass",
        component: JobsPageComponent,
        canActivate: [StaffGuard, SubscriptionGuard],
        children: [
            {
                // Static path must precede the `:itemId` param below so it is matched first.
                path: "instance/:instanceName",
                component: JobInstanceDetailsComponent,
                canActivate: [StaffGuard, SubscriptionGuard],
            },
            {
                path: ":itemId",
                component: JobDetailsComponent,
                canActivate: [StaffGuard, SubscriptionGuard],
                children: [
                    {
                        path: ":jobTaskId",
                        component: JobTaskDetailsComponent,
                        canActivate: [StaffGuard, SubscriptionGuard],
                    },
                ],
            },
        ],
    },

    {
        path: "login",
        title: "Login - colormass",
        component: LoginPageComponent,
        canActivate: [RedirectIfAuthenticatedGuard],
    },

    {
        path: "login/okta/:slug",
        title: "Sign In - colormass",
        component: TenantLoginPageComponent,
        canActivate: [RedirectIfAuthenticatedGuard],
    },

    {
        path: "95ngsj3e/sign-up",
        title: "Sign Up - colormass",
        component: SignUpPageComponent,
        canActivate: [RedirectIfAuthenticatedGuard],
    },

    {
        path: "verify-email",
        title: "Verify Email - colormass",
        component: VerifyEmailPageComponent,
    },

    {
        path: "onboarding",
        title: "Welcome - colormass",
        component: OnboardingPageComponent,
        canActivate: [OnboardingGuard],
    },

    {
        path: "manual-testing",
        component: TestBaseComponent,
        canActivate: [AuthGuard, StaffGuard, SubscriptionGuard],
    },

    {
        ...materialsRouteBase,
        path: "materials/library",
        title: "Material Library - colormass",
        data: {viewMode: "materialLibrary"},
        children: [
            {
                path: "create",
                data: {createDialog: true, viewMode: "materialLibrary"},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            ...materialsRouteBase.children.filter((child) => child.path !== "create"),
        ],
    },

    {
        ...materialsRouteBase,
        path: "materials",
        title: "Materials - colormass",
    },

    {
        path: "models",
        title: "Models - colormass",
        component: ModelsPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            {
                path: ":itemId",
                component: ModelDetailsComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [
                    {
                        path: "tasks/:taskId",
                        component: TaskDetailsComponent,
                        canActivate: [AuthGuard, SubscriptionGuard],
                        data: {closeNavigationPath: "../../"},
                    },
                ],
            },
        ],
    },

    {
        path: "organization-groups",
        title: "Organization Groups - colormass",
        component: OrganizationGroupsPageComponent,
        canActivate: [StaffGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [StaffGuard, SubscriptionGuard],
                children: [],
            },
        ],
    },

    {
        path: "organizations",
        title: "Organizations - colormass",
        component: OrganizationsPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            {
                path: ":itemId",
                component: OrganizationDetailsComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
            },
        ],
    },

    {
        path: "pictures",
        title: "Pictures - colormass",
        component: PicturesPageComponent,
        canActivate: [PicturesAuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            {
                path: ":itemId",
                redirectTo: ":itemId/revisions/latest",
                pathMatch: "full",
            },
            {
                path: ":itemId/revisions",
                redirectTo: ":itemId/revisions/latest",
                pathMatch: "full",
            },
            {
                path: ":itemId/revisions/new",
                component: PictureDetailsComponent,
                data: {closeNavigationPath: "../../../"},
                canActivate: [AuthGuard, SubscriptionGuard],
                resolve: {legacyPicture: LegacyPictureResolve},
                children: [
                    {
                        path: "tasks/:taskId",
                        component: TaskDetailsComponent,
                        canActivate: [AuthGuard, SubscriptionGuard],
                        data: {closeNavigationPath: "../../"},
                    },
                ],
            },
            {
                path: ":itemId/revisions/:revisionNumber",
                component: PictureDetailsComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
                data: {closeNavigationPath: "../../../"},
                resolve: {legacyPicture: LegacyPictureResolve},
                children: [
                    {
                        path: "view",
                        data: {closeNavigationPath: "../"},
                        component: PictureViewerDialogComponent,
                        canActivate: [AuthGuard, SubscriptionGuard],
                    },
                    {
                        path: "tasks/:taskId",
                        component: TaskDetailsComponent,
                        canActivate: [AuthGuard, SubscriptionGuard],
                        data: {closeNavigationPath: "../../"},
                    },
                ],
            },
            {
                path: ":pictureId/template-revisions/add",
                component: TemplateEditorComponent,
                data: {closeNavigationPath: "../../../", ensureScenePropertiesNodeExists: true},
                canActivate: [AuthGuard, SubscriptionGuard],
            },
            {
                path: ":pictureId/template-revisions/:templateRevisionId/edit",
                component: TemplateEditorComponent,
                data: {closeNavigationPath: "../../../../"},
                canActivate: [AuthGuard, SubscriptionGuard],
            },
            {
                path: ":pictureId/view",
                component: TemplateViewerDialogComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
            },
        ],
    },

    {
        path: "price-mapping",
        title: "Price Mapping - colormass",
        component: PriceMappingComponent,
        canActivate: [AuthGuard, StaffGuard],
    },

    {
        ...templateRouteBase,
        path: "products",
        title: "Products - colormass",
        data: {viewMode: "products"},
        children: [
            {
                path: "create",
                data: {createDialog: true, viewMode: "products"},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            ...templateRouteBase.children,
        ],
    },

    {
        path: "render-node-groups",
        title: "Render Node Groups - colormass",
        component: JobFarmGroupsPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
        ],
    },

    {
        path: "render-nodes",
        title: "Render Nodes - colormass",
        component: JobFarmInstancesPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
    },

    {
        path: "scanning",
        title: "Scanning - colormass",
        component: ScanningComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "logs",
                component: ScanningLogsComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
            },
        ],
    },

    {
        ...templateRouteBase,
        path: "scenes",
        title: "Scenes - colormass",
        data: {viewMode: "scenes"},
        children: [
            {
                path: "create",
                data: {createDialog: true, viewMode: "scenes"},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            ...templateRouteBase.children,
        ],
    },

    {
        path: "settings",
        title: "Settings",
        children: [
            {
                path: "",
                redirectTo: "organization",
                pathMatch: "full",
            },
            {
                path: "organization",
                title: "Organization Settings - colormass",
                component: OrganizationSettingsComponent,
                canActivate: [OrganizationAdminGuard],
            },
        ],
    },

    {
        path: "sso",
        title: "Single Sign-On - colormass",
        children: [
            {
                path: "google",
                component: HandleSsoCallbackComponent,
                data: {
                    provider: "google",
                },
            },
            {
                path: "microsoft",
                component: HandleSsoCallbackComponent,
                data: {
                    provider: "microsoft",
                },
            },
            {
                path: "okta",
                component: HandleSsoCallbackComponent,
                data: {
                    provider: "richemont",
                },
            },
            {
                path: "google/signup",
                component: HandleSsoSignupCallbackComponent,
                data: {
                    provider: "google",
                },
            },
            {
                path: "microsoft/signup",
                component: HandleSsoSignupCallbackComponent,
                data: {
                    provider: "microsoft",
                },
            },
        ],
    },

    {
        path: "statistics",
        title: "Statistics - colormass",
        component: StatisticsPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
    },

    {
        path: "tags",
        title: "Tags - colormass",
        component: TagsPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            {
                path: ":itemId",
                component: TagDetailsComponent,
                canActivate: [AuthGuard, SubscriptionGuard],
            },
        ],
    },

    {
        path: "task-types",
        title: "Task Types - colormass",
        component: TaskTypesPageComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
        ],
    },

    {
        ...templateRouteBase,
        path: "templates",
        title: "Templates - colormass",
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [AuthGuard, SubscriptionGuard],
                children: [],
            },
            ...templateRouteBase.children,
        ],
    },

    {
        path: "unauthorized",
        title: "Unauthorized - colormass",
        component: UnauthorizedComponent,
    },

    {
        path: "users",
        title: "Users - colormass",
        component: UsersPageComponent,
        canActivate: [StaffGuard],
        children: [
            {
                path: "create",
                data: {createDialog: true},
                canActivate: [StaffGuard],
                children: [],
            },
            {
                path: ":itemId",
                component: UserDetailsComponent,
                canActivate: [StaffGuard],
            },
        ],
    },

    {
        path: "**",
        component: PageNotFoundComponent,
    },
]
